import memoize from 'lodash/memoize';
import WebComponent from 'webcomponent';

import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {isNodeEnv} from 'common/report/util/env';
import {panel2react} from 'common/util/react';

import {SVG_ICONS} from './icons';

export type SvgIconType = keyof typeof SVG_ICONS;

// NOTE: This component depends on styles that aren't packaged with it
// as there's no reliable, efficient way to do so.
// Option 1: Inject the CSS into the <head> tag
//   Problem: Styles don't get applied to svg-icons that happen to be in
//   ShadowDOM (like inside an mp-button component)
// Option 2: Use ShadowDOM for this component
//   Problem: This makes it a poor experience for styling the icons
//   from the "outside" which we'll often want to do for colors and
//   positioning.
// Option 3: Expect the styles to be handled separately. This is choice
//   we've made here. They come "for free" when you include "default" styles for mixpanel-common
//   which should happen for pretty much every page and every component.

export const SVG_ICON_NAMES = Object.keys(SVG_ICONS) as SvgIconType[];

function generateIconTemplate(iconName: string): null | HTMLTemplateElement {
  const markup = SVG_ICONS[iconName];
  if (markup) {
    const templateElement = document.createElement(`template`);
    // This is trusted markup coming from local files we control
    // eslint-disable-next-line no-unsanitized/property
    templateElement.innerHTML = markup;
    return templateElement;
  }
  return null;
}

const getIconTemplate = memoize(generateIconTemplate);

export type SvgIconProps = {
  icon: SvgIconType;
  empty?: boolean;
  color?: string;
  height?: string;
};

export type SvgIconJsxProps = {
  attrs: SvgIconProps;
};

export class SvgIcon extends WebComponent {
  static get observedAttributes() {
    return [`empty`, `icon`, `color`];
  }

  connectedCallback() {
    this.render();
  }

  attributeChangedCallback() {
    this.render();
  }

  render() {
    const icon = this.getAttribute(`icon`);
    const empty = this.isAttributeEnabled(`empty`);
    this.innerHTML = ``;
    if (icon && !empty) {
      // the template.content DocumentFragment doesn't work in Node.js, just fallback to raw innerHTML
      if (isNodeEnv() && SVG_ICONS[icon]) {
        // eslint-disable-next-line no-unsanitized/property
        this.innerHTML = SVG_ICONS[icon];
        return;
      }

      const baseFragment = getIconTemplate(icon)?.content;
      if (!baseFragment) {
        throw new Error(`No svg-icon "${icon}" found.`);
      }

      const iconFragment = baseFragment.cloneNode(true) as DocumentFragment;
      const color = this.getAttribute(`color`);
      if (color) {
        const svg = iconFragment.querySelector(`svg`);
        if (svg) {
          svg.style.color = color;
        }
      }

      const height = this.getAttribute(`height`);
      if (height) {
        const svg = iconFragment.querySelector(`svg`);
        if (svg) {
          svg.style.height = height;
        }
      }

      this.appendChild(iconFragment);
    }
  }
}

export const SvgIconEl = defineAndMakeJsxFactory(`svg-icon`, SvgIcon);
export const SvgIconReact = panel2react<SvgIconJsxProps>(`svg-icon`, SvgIcon);
